import React, { useEffect, Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import moment from 'moment';
//Components
import {
    SidePanelHeader,
    SidePanelBody,
    MenuComponent,
    SearchInput,
    IconButton,
    SidePanel,
    TableView,
    Link,
    Pill,
} from '@gpstrackit/gtc-react-controls';
import { Grid, MenuItem } from '@mui/material';
import { NotificationManager } from 'Components/Notifications';
import ListItemText from '@mui/material/ListItemText';
import OdometerView, { getOdometerLabel } from '../../../../../.common/components/OdometerView';

//Views
import ExportVehicles from './exportVehicles';
import CompleteMaintenanceService from '../../../../modals/completeMaintenanceService';
import MaintenanceServicesToComplete from '../../../../modals/maintenanceServicesToComplete';


//Hooks
import {
    formatOffsetDisplayEngineHours,
    formatOffsetDisplayOdometer,
    usePaggination
} from '../../../../../../core/hooks';


//Actions
import { openModalScheduleMaintenanceServiceUnit, setHistoryDTC, openOverViewUnit } from 'Redux/actions';

//Styles
import { ExportButton } from 'Components/Table/Toolbar';
import styles from './styles.scss';

//Actions
import {
    openModalExportVehicles,
    dtcGetDeviceDTC,
    catalogLoad,
    openModalCompleteMaintenance,
    openCloseModalMaintenanceServicesToComplete,
    clearRedux,
    sendGenericExport,
} from 'Redux/actions';

import {
    maintenanceVTGetServicesList,
    maintenanceVTGetVehiclesData,
    changeFilters,
    changeSort,
    reset,
} from './redux/slice';
import { convertToKPH } from '../../../../../../core/data/Helpers';
import TagsManage from 'Modules/.common/components/TagsManage';
import StatusFilter from './components/statusFilter';
import FilterServices from './components/servicesFilter';
import VehicleFilter from './components/vehicleFilter';

const VEHICLES_TAB_DEFAULT_FILTERS = {
    status: [-1],
    groups: [-1],
    tags: [],
    services: [],
    vehicles: [],
};

const normalizeIdArray = (values = []) => {
    return values
        .map((value) => {
            if (value === undefined || value === null) return null;
            const stringValue = `${value}`.trim();
            if (!stringValue) return null;

            const numericValue = Number(stringValue);
            if (Number.isFinite(numericValue) && numericValue > 0 && /^\d+$/.test(stringValue)) {
                return numericValue;
            }

            return stringValue;
        })
        .filter((value) => value !== null);
};

const normalizeFilters = (filterValues = VEHICLES_TAB_DEFAULT_FILTERS) => {
    return {
        status: (filterValues?.status || []).filter((value) => value !== undefined && value !== null),
        groups: (filterValues?.groups || []).filter((value) => value !== undefined && value !== null),
        tags: (filterValues?.tags || []).filter((value) => value?.id !== undefined && value?.id !== null),
        services: normalizeIdArray(filterValues?.services || []),
        vehicles: normalizeIdArray(filterValues?.vehicles || []),
    };
};

const areFiltersEqual = (a, b) => {
    return JSON.stringify(normalizeFilters(a)) === JSON.stringify(normalizeFilters(b));
};

const isFiltersSliceUninitialized = (f) => {
    if (!f) return true;
    return (
        (!f.status || f.status.length === 0) &&
        (!f.groups || f.groups.length === 0) &&
        (!f.tags || f.tags.length === 0) &&
        (!f.services || f.services.length === 0) &&
        (!f.vehicles || f.vehicles.length === 0)
    );
};

const VehiclesTab = () => {

    //constants
    const dispatch = useDispatch();
    const tableRef = useRef();
    const menuRef = useRef();
    const navigate = useNavigate();
    const { messages, locale } = useIntl();
    const pagination = usePaggination(maintenanceVTGetVehiclesData, ["label"], false, tableRef);
    const { setColumns, addConditions, ...tablePagination } = pagination;
    const addConditionsRef = useRef(addConditions);
    addConditionsRef.current = addConditions;

    const { items, total, filters, sort: savedSort, error, loadingVehicles: loading } = useSelector(state => state.maintenanceVehiclesTabRedux);

    //Redux States
    const openExportVehicles = useSelector(state => state.maintenanceExportVehiclesRedux.open);
    const openCompleteMaintenanceService = useSelector(state => state.maintenanceCompleteMaintenanceServiceRedux.open);
    const rawFilters = useSelector(state => state.maintenanceVehiclesTabRedux.filters);
    const openMaintenanceServicesToComplete = useSelector(state => state.maintenanceServicesToCompleteRedux.open);
    const { exportResult, exportName } = useSelector(state => state.commomRedux);
    const user = useSelector(state => state.securityRedux.user);

    //Local States
    const [showSidePanel, setShowSidePanel] = useState(false);
    const [currentVehicle, setCurrentVehicle] = useState();

    const optionsMenu = useMemo(
        () => [
            messages['vehicleView_scheduleService'],
            messages['vehicleView_viewScheduledervices'],
            messages['vehicleView_viewDiagnosticHistory'],
            messages['unitOverViewPanel'],
        ],
        [locale]
    );

    const hasActiveVehicleFilters = useMemo(() => {
        if (!filters) return false;
        const { status = [], groups = [], tags = [], services = [], vehicles: vehicleIds = [] } = filters;
        if ((tags || []).length > 0) return true;
        if ((services || []).length > 0) return true;
        if ((vehicleIds || []).length > 0) return true;
        if ((status || []).some((x) => x !== -1)) return true;
        if ((groups || []).some((x) => x !== -1)) return true;
        return false;
    }, [filters]);

    const buildConditionsFromFilters = useCallback((filtersValue) => {
        const conditions = [];
        const normalizedFilters = normalizeFilters(filtersValue || {});
        const { status, tags, services, vehicles } = normalizedFilters;

        const realStatus = (status || []).filter(x => x !== -1);
        if (realStatus.length > 0) {
            conditions.push({
                fields: ["nextMaintenanceStatus"],
                terms: realStatus,
            });
        }

        let tagIds = [];
        if (tags?.length) {
            tagIds = tags.map(tag => tag?.id).filter(id => id !== undefined && id !== null);
        }

        if (tagIds.length && tagIds[0] != '-99') {
            conditions.push({
                fields: ["tagIds"],
                terms: tagIds.map(id => id.toString()),
                exact: true,
            });
        }

        if ((vehicles || []).length > 0) {
            conditions.push({
                fields: ["id"],
                terms: vehicles,
            });
        }

        if ((services || []).length > 0) {
            conditions.push({
                fields: ["nextMaintenanceServiceUnit_serviceId"],
                terms: services,
            });
        }

        return conditions;
    }, []);

    //handle change filters
    const onChangeFilters = useCallback((newFilters) => {
        if (areFiltersEqual(filters, newFilters)) {
            return;
        }

        const normalizedFilters = normalizeFilters(newFilters);
        tableRef?.current?.setPage(1);
        dispatch(changeFilters(normalizedFilters));
        const conditions = buildConditionsFromFilters(normalizedFilters);
        addConditionsRef.current(1, conditions);
    }, [buildConditionsFromFilters, dispatch, filters]);

    const onOpenDiagnosticCodes = useCallback((vehicle) => {
        dispatch(setHistoryDTC({ history: (vehicle?.dtcCount?.active > 0 ? false : true) }))
        dispatch(dtcGetDeviceDTC({ device: vehicle, history: (vehicle?.dtcCount?.active > 0 ? false : true) }));
    }, []);

    useEffect(() => {
        loadInitialData();
        const sortToApply = savedSort || { key: 'nextMaintenanceStatus', order: 'asc' };
        tableRef.current?.setSort(sortToApply);
        pagination.setSort(sortToApply);
    }, []);

    useEffect(() => {
        if (error) {
            dispatch(reset({ error: null }))
        }
    }, [error, dispatch])

    useEffect(() => {
        setColumns([
            {
                key: "label",
                sort: true,
                show: true,
                width: 250,
                title: messages["vehicleView_vehicle"],
                sortValue: (vehicle) => vehicle?.label,
                export: true,
                functionString: 'return row?.label || ""',
                render: (vehicle) => {
                    let _optionsMenu = [...optionsMenu];
                    if (vehicle.nextMaintenanceServiceUnit?.status && !_optionsMenu?.find(op => op === messages['vehicleView_viewCompleteMaintenance'])) {
                        _optionsMenu.unshift(messages['vehicleView_viewCompleteMaintenance']);
                    }

                    return (
                        <Grid container alignItems="center">
                            <Grid item xs={9} style={{ width: 250 }}>
                                <Link
                                    className="table-span"
                                    dest={`/app/maintenance/vehicleDetail/${vehicle.id}`}
                                    onClick={() => navigate(`/app/maintenance/vehicleDetail/${vehicle.id}`)}
                                    text={vehicle?.label}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <MenuComponent
                                    className="showInHover"
                                    options={_optionsMenu}
                                    onClick={(type, value) => {
                                        if (type === messages['vehicleView_scheduleService']) {
                                            if (navigate) {
                                                navigate(`/app/maintenance/vehicleDetail/${value.id}`);
                                                setTimeout(() => {
                                                    dispatch(openModalScheduleMaintenanceServiceUnit({ open: true }));
                                                }, 50);
                                            }
                                        } else if (type === messages['vehicleView_viewScheduledervices']) {
                                            if (navigate) {
                                                navigate(`/app/maintenance/vehicleDetail/${value.id}`);
                                            }
                                        } else if (type === messages['vehicleView_viewDiagnosticHistory']) {
                                            onOpenDiagnosticCodes(value);

                                        } else if (type === messages['unitOverViewPanel']) {
                                            dispatch(
                                                openOverViewUnit({
                                                    data: {
                                                        incident: {},
                                                        reading: {},
                                                        unit: value
                                                    },
                                                    open: true,
                                                })
                                            )

                                        } else if (type === messages['vehicleView_viewCompleteMaintenance']) {
                                            setCurrentVehicle(() => {
                                                return value;
                                            });

                                            dispatch(openCloseModalMaintenanceServicesToComplete({ open: true, vehicle: value }));
                                        }
                                    }}
                                    icon="more_vert"
                                    value={vehicle}
                                />
                            </Grid>
                        </Grid>
                    );
                },
            },
            {
                key: "nextMaintenanceStatus",
                sort: true,
                show: true,
                width: 150,
                title: messages["maintenance_nextService"],
                export: true,
                functionString: `let s = row?.nextMaintenanceServiceUnit?.status || row?.nextMaintenanceStatus; let m = { PastDue: "${messages["vehicleView_pastDue"]}", Due: "${messages["vehicleView_dueStatus"]}", DueSoon: "${messages["dueSoon"]}", Scheduled: "${messages["vehicleView_scheduledStatus"]}", Done: "${messages["done"]}" }; return s ? (m[s] || s) : ""`,
                render: (vehicle) => {
                    if (!vehicle.nextMaintenanceServiceUnit?.status) {
                        return <span />;
                    }

                    const { tooltip, color } = getStatus(vehicle)
                    return (
                        <Pill
                            style={{ width: 110 }}
                            item={{ label: tooltip, color: color }}
                            onClick={() => {
                                setCurrentVehicle(() => {
                                    return vehicle;
                                });
                                dispatch(openModalCompleteMaintenance({ open: true, serviceUnitId: vehicle?.nextMaintenanceServiceUnit?.id }))
                            }}
                        />
                    );
                },
                sortValue: (vehicle) => {
                    switch (vehicle.nextMaintenanceServiceUnit?.status || vehicle.nextMaintenanceStatus) {
                        case "PastDue":
                            return 1
                        case "Due":
                            return 2
                        case "DueSoon":
                            return 3
                        case "Scheduled":
                            return 4
                        case "Done":
                            return 5
                        default:
                            return 6
                    }
                },
            },
            {
                key: "engineHours",
                sort: true,
                show: true,
                width: 170,
                title: messages["vehicleView_engineRunTime"],
                export: true,
                functionString: `var base = row?.useEcuEngineHours ? parseFloat(row?.lastReading?.ecuEngineHours) : parseFloat(row?.lastReading?.engineHours); var offset = row?.useEcuEngineHours ? parseFloat(row?.offsetEcuEngineHours || 0) : parseFloat(row?.offsetEngineHours || 0); if (isNaN(base)) base = 0; var eh = base + offset; if (eh < 0) eh = 0; return eh.toFixed(2) + " ${messages['hours']}"`,
                render: (vehicle) => {
                    let label = vehicle?.displayEngineHours
                    label = getEngineHoursLabel(label)
                    return (
                        <div>
                            {vehicle.useEcuEngineHours && (
                                <div
                                    className="ecuChip"
                                    style={{ background: styles.ecu }}
                                >
                                    <span>ECU</span>
                                </div>
                            )}
                            <span className="default_padding">
                                {" " + label}
                            </span>
                        </div>
                    );
                },
                sortField: "engineHoursTotal",
                sortValue: (vehicle) => {
                    const displayEngineHours = vehicle?.displayEngineHours
                    const data = displayEngineHours ? displayEngineHours?.split(" ") : []
                    const value = data.length ? parseFloat(data[0]) : 0
                    return value
                }

            },
            {
                key: "engineOdometer",
                sort: true,
                show: true,
                width: 150,
                title: messages["vehicleView_odometer"],
                export: true,
                functionString: `var base = row?.useEcuOdometer ? parseFloat(row?.lastReading?.ecuEngineOdometer) : parseFloat(row?.lastReading?.engineOdometer); var offset = row?.useEcuOdometer ? parseFloat(row?.offsetEcuEngineOdometer || 0) : parseFloat(row?.offsetEngineOdometer || 0); if (isNaN(base)) base = 0; var o = Math.round(base + offset); if (o < 0) o = 0; var isKph = ${user?.isKph}; return (isKph ? (o * 1.60934).toFixed(0) : o) + (isKph ? " km" : " mi")`,
                render: (vehicle) => {
                    const odoValue = getOdometerLabel({
                        engineOdometer: vehicle.lastReading?.engineOdometer || 0,
                        ecuEngineOdometer: vehicle.lastReading?.ecuEngineOdometer || 0,
                        useEcuOdometer: vehicle.useEcuOdometer,
                        offsetEcuEngineOdometer: vehicle.offsetEcuEngineOdometer,
                        offsetEngineOdometer: vehicle.offsetEngineOdometer,
                        isKph: user?.isKph,
                    });
                    const label = odoValue ? `${Number(odoValue).toLocaleString('en-US')} ${user?.isKph ? 'km' : 'mi'}` : '';
                    return (
                        <div>
                            {vehicle.useEcuOdometer && (
                                <div
                                    className="ecuChip"
                                    style={{ background: styles.ecu }}
                                >
                                    <span>ECU</span>
                                </div>
                            )}
                            <span className="default_padding">
                                {' ' + label}
                            </span>
                        </div>
                    );
                },
                sortField: "odometerTotal",
                sortValue: (vehicle) => Number(vehicle.displayOdometer)
            },
            {
                key: "dtc",
                sort: false,
                show: true,
                width: 100,
                title: messages["vehicleView_diagnosticCode"],
                export: true,
                functionString: 'return (row?.dtcCount?.active != null ? row.dtcCount.active : "") + (row?.dtcCount?.history ? " / " + row.dtcCount.history : "")',
                render: (vehicle) => {
                    if (vehicle?.dtcCount?.active == 0 && vehicle?.dtcCount?.history == 0) {
                        return <span />;
                    }
                    let color = "black";
                    let icon = "history";
                    if (vehicle?.dtcCount?.active > 0) {
                        color = "red";
                        icon = "warning";
                    }
                    return (
                        <IconButton
                            icon={icon}
                            tooltip={messages["dtc_codes"]}
                            placement="top"
                            style={{ color: color, fontSize: 18 }}
                            onClick={() => {
                                onOpenDiagnosticCodes(vehicle);
                            }}
                        />
                    );
                },
                sortValue: (vehicle) => {
                    if (vehicle?.dtcCount?.active > 0) {
                        return vehicle?.dtcCount?.active + 1000;
                    } else if (vehicle?.dtcCount?.history > 0) {
                        return vehicle?.dtcCount?.history;
                    }
                    return 0;
                }
            },
            {
                key: "lastMaintenanceServiceUnitLog",
                sort: true,
                show: true,
                width: 250,
                title: messages["maintenance_lastServicePerformed"],
                export: true,
                functionString: `var log = row?.lastMaintenanceServiceUnitLog; if (!log) return ""; var d = log.maintenanceDate ? new Date(log.maintenanceDate).toLocaleString() : ""; var t = log?.serviceUnit?.service?.title || ""; var o = log?.odometer; var u = ${user?.isKph}; if (o != null && !isNaN(o)) o = (u ? (o * 1.60934).toFixed(0) : o) + (u ? " km" : " mi"); return d + " / " + t + (o ? " / " + o : "")`,
                sortValue: (vehicle) => {
                    return vehicle.lastMaintenanceServiceUnitLog && `${vehicle.lastMaintenanceServiceUnitLog.maintenanceDate && moment(vehicle.lastMaintenanceServiceUnitLog.maintenanceDate).format("MMM DD, YYYY [at] h:mm a") || ""} / ${vehicle.lastMaintenanceServiceUnitLog?.serviceUnit?.service?.title || ""}`
                },
                sortField: "lastServiceTitle",
                render: (vehicle) => {
                    return (
                        <div>
                            {getLastServicePerformedLabel(vehicle)}
                        </div>
                    );
                },
            },
        ])
    }, [setColumns, locale, user, navigate, dispatch, onOpenDiagnosticCodes, optionsMenu])

    useEffect(() => {
        if (exportResult && exportName === "maintenance_vehicles") {
            NotificationManager.info(
                messages["exportInProcessBody"],
                messages["exportInProcessTitle"],
                4000,
                null,
                null,
                "info",
                null
            );
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [exportResult, exportName, dispatch, messages]);

    const computedData = useMemo(() => {
        const list = items || [];
        return list.map(unit => {
            const unitToPush = { ...unit, ...unit.lastReading };

            unitToPush.displayOdometer = formatOffsetDisplayOdometer(unitToPush);
            unitToPush.displayEngineHours = formatOffsetDisplayEngineHours(unitToPush);
            unitToPush.displayHours = formatOffsetDisplayEngineHours(unitToPush);
            unitToPush.hoursEnable = unitToPush.unitVersion?.hoursEnable;
            unitToPush.odometerEnable = unitToPush.unitVersion?.odometerEnable;

            if (unitToPush.displayEngineHours && unitToPush.displayEngineHours.length !== "") {
                unitToPush.displayEngineHours += " " + messages["hours"];
            }

            return unitToPush;
        });
    }, [items, messages]);

    const loadInitialData = () => {
        dispatch(maintenanceVTGetServicesList({}));

        const filtersToUse = isFiltersSliceUninitialized(rawFilters)
            ? VEHICLES_TAB_DEFAULT_FILTERS
            : normalizeFilters(rawFilters);

        if (isFiltersSliceUninitialized(rawFilters)) {
            dispatch(changeFilters(VEHICLES_TAB_DEFAULT_FILTERS));
        }

        const conditions = buildConditionsFromFilters(filtersToUse);
        addConditionsRef.current(1, conditions);
    };

    const getLastServicePerformedLabel = (vehicle) => {
        if (vehicle?.lastMaintenanceServiceUnitLog) {
            return vehicle.lastMaintenanceServiceUnitLog &&
                `${(vehicle.lastMaintenanceServiceUnitLog.maintenanceDate &&
                    moment(
                        vehicle.lastMaintenanceServiceUnitLog.maintenanceDate
                    ).format("MMM DD, YYYY [at] h:mm a")) ||
                ""
                } / ${vehicle.lastMaintenanceServiceUnitLog?.serviceUnit
                    ?.service?.title || ""
                } ${getOdometerFormat(vehicle.lastMaintenanceServiceUnitLog?.odometer)}`
        }
        return "";
    }

    const getEngineHoursLabel = (label) => {
        return label ? label.replace('Hours', messages['hours']).replace('Horas', messages['hours']) : ""
    }

    const getOdometerFormat = (odometer) => {
        if (odometer && user.isKph) {
            odometer = convertToKPH(odometer)
        }
        return (odometer != undefined && odometer != NaN ? `/ ${odometer?.toLocaleString('en-US')} ${user?.isKph ? "km" : "mi"}` : "");
    }

    const getStatus = (vehicle) => {
        let color = "";
        let tooltip = "";

        switch (vehicle.nextMaintenanceServiceUnit?.status) {
            case "PastDue":
                color = styles.purple;
                tooltip = messages["vehicleView_pastDue"];
                break;
            case "Due":
                color = styles.red;
                tooltip = messages["vehicleView_dueStatus"];
                break;
            case "DueSoon":
                color = styles.orange;
                tooltip = messages["dueSoon"];
                break;
            case "Scheduled":
                color = styles.green;
                tooltip = messages["vehicleView_scheduledStatus"];
                break;
            case "Done":
                color = styles.black;
                tooltip = messages["done"];
                break;
        }
        return { color, tooltip }
    }

    const MAINTENANCE_VEHICLES_QUERY = `
        query MaintenanceVehicles($conditions: [MaintenanceUnitSearchInput!], $sort: [MaintenanceUnitSortInput!], $limit: Int, $offset: Int) {
            data: searchMaintenanceUnits(conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                items {
                    id
                    label
                    jbus
                    vbus
                    useEcuEngineHours
                    useEcuOdometer
                    offsetEcuEngineOdometer
                    offsetEngineOdometer
                    offsetEcuEngineHours
                    offsetEngineHours
                    nextMaintenanceServiceUnit { id status serviceId }
                    tags { id label }
                    lastReading { engineOdometer ecuEngineOdometer engineHours ecuEngineHours }
                    lastMaintenanceServiceUnitLog {
                        id maintenanceDate odometer
                        serviceUnit { service { title } }
                    }
                    dtcCount { history active }
                }
                total
            }
        }
    `;

    const onExport = (type) => {
        if (type === "upcomingmaintenance") {
            dispatch(openModalExportVehicles({ open: true }));
            return;
        }
        if (type === "CSV" || type === "PDF") {
            const currentConditions = pagination.currentConditions || [];
            const currentSort = pagination.currentSort;
            const columns = pagination?.columns || [];
            const headers = columns
                .filter((x) => x.show && x.export)
                .map((x) => ({ header: x.title, column: x.key, functionString: x.functionString }));
            const columnStyles = { 6: { cellWidth: 140 } };
            dispatch(
                sendGenericExport(
                    "maintenance_vehicles",
                    type === "CSV" ? "Csv" : "Pdf",
                    MAINTENANCE_VEHICLES_QUERY,
                    {
                        conditions: currentConditions,
                        sort: currentSort ? [currentSort] : [],
                        limit: 10,
                        offset: 0,
                    },
                    headers,
                    { unit: "px", size: "A4", orientation: "landscape", fontSize: 10 },
                    null,
                    null,
                    true,
                    columnStyles
                )
            );
        }
    };

    return (
        <Fragment>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: 'flex' }}>
                            <IconButton
                                tooltip={messages["table_filter"]}
                                placement="bottom"
                                variant={hasActiveVehicleFilters ? '' : 'secondary'}
                                icon="filter_list"
                                size="small"
                                isDisabled={loading}
                                className={hasActiveVehicleFilters ? 'select-view-icon-filter' : 'select-view-icon'}
                                onClick={() => setShowSidePanel(!showSidePanel)}
                            />
                            <ExportButton
                                onExport={onExport}
                                className="webHookExportButton"
                                menuRef={menuRef}
                            >
                                <MenuItem
                                    onClick={(e) => {
                                        onExport('upcomingmaintenance');
                                        menuRef.current.closeMenu();
                                    }}
                                >
                                    <ListItemText primary={messages["exportVehicles_upcoming"]} />
                                </MenuItem>
                            </ExportButton>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages['search']}
                        searchText={pagination.searchText}
                        onSearchText={pagination.onSearchChange}
                        isDisabled={loading}
                    />
                </Grid>
            </Grid>

            <TableView
                ref={tableRef}
                keyColumn="id"
                data={computedData || []}
                total={total || 0}
                loading={loading}
                disableItemSelection
                {...tablePagination}
                onChangeSort={(newSort) => {
                    tablePagination.onChangeSort(newSort);
                    dispatch(changeSort(newSort));
                }}
                topheight={250}
                hideDragable
                hideAllInPagination
            />
            <SidePanel
                isOpen={showSidePanel}
                onToggle={() => setShowSidePanel(!showSidePanel)}
                style={{ width: '350px', transform: 'translateX(350px)' }}
                classSidePanel={hasActiveVehicleFilters ? 'maintenance-vehicles-filters-active' : ''}
            >
                <SidePanelHeader>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <h2 style={{ margin: 0 }}>{messages['vehicleView_advancedFilters']}</h2>
                        </Grid>

                        <Grid item className="text-right">
                            <IconButton
                                type="icon"
                                icon="delete"
                                tooltip={messages['vehicleView_clearFilters']}
                                placement="left"
                                onClick=
                                {() => {
                                    onChangeFilters({
                                        status: [-1],
                                        groups: [-1],
                                        tags: [],
                                        services: [],
                                        vehicles: [],
                                    })
                                }}
                            />
                        </Grid>
                    </Grid>
                </SidePanelHeader>
                <SidePanelBody>
                    {
                        showSidePanel &&
                        <div style={{ marginBottom: 8 }}>
                            <p style={{ fontWeight: "bold", fontSize: "14px", lineHeight: "0", paddingBottom: "5px" }}>{messages["vehicleView_tags"]}</p>
                            <TagsManage
                                enableSelectAll={true}
                                onlyTags={true}
                                mode="list"
                                value={filters?.tags || []}
                                onChange={(items) => onChangeFilters({ ...filters, tags: items })}
                                height={300}
                            />
                        </div>
                    }
                    <div>
                        <StatusFilter
                            selectedStatus={filters?.status || [-1]}
                            onChange={(items) => {
                                const currentStatus = (filters?.status || []).filter((id) => id !== -1);
                                let nextStatus = [];

                                if (Array.isArray(items)) {
                                    nextStatus = items
                                        .map((x) => x?.id)
                                        .filter((id) => id !== undefined && id !== null && id !== -1);
                                } else if (items?.id !== undefined && items?.id !== null) {
                                    const clickedId = items.id;
                                    if (clickedId !== -1) {
                                        nextStatus = currentStatus.includes(clickedId)
                                            ? currentStatus.filter((id) => id !== clickedId)
                                            : [...currentStatus, clickedId];
                                    }
                                }

                                onChangeFilters({
                                    ...filters,
                                    status: nextStatus.length ? nextStatus : [-1],
                                });
                            }}
                        />
                    </div>
                    <div style={{ paddingTop: 20 }}>
                        <FilterServices
                            value={(x) => (filters.services || []).some(id => `${id}` === `${x}`)}
                            onChange={(items = []) => {
                                const services = normalizeIdArray((items || []).map(x => x?.id));
                                onChangeFilters({ ...filters, services })
                            }}
                        />
                    </div>
                    <div style={{ paddingTop: 20, marginBottom: 250 }}>
                        <VehicleFilter
                            value={(x) => (filters.vehicles || []).some(id => `${id}` === `${x}`)}
                            onChange={(items) => {
                                const vehicles = normalizeIdArray((items || []).map(x => x?.id));
                                onChangeFilters({ ...filters, vehicles })
                            }}
                        />
                    </div>
                </SidePanelBody>
            </SidePanel>

            {openExportVehicles && <ExportVehicles />}

            {
                openCompleteMaintenanceService &&
                <CompleteMaintenanceService
                    vehicle={currentVehicle}
                    refresh={() => {
                        dispatch(maintenanceVTGetVehiclesData({}));
                    }}
                />
            }

            {openMaintenanceServicesToComplete && <MaintenanceServicesToComplete />}
        </Fragment>
    );
};

export default VehiclesTab;